/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Admin AI Models & Providers Management View (Task 11.2.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  RefreshCw,
  Edit2,
  Trash2,
  CheckCircle2,
  XCircle,
  Star,
  Search,
  Check,
  AlertTriangle,
  X,
  Sliders,
} from 'lucide-react';
import { useI18n } from '../i18n';
import { adminClient } from '../lib/api-client';
import { DataTable, type ColumnDef } from '../components/table/DataTable';
import { getBrandColor, SophiaIcon, type SophiaDefaultIconName } from '@sofia/assets';
import type {
  AdminAiModel,
  AdminAiProvider,
  AiModelModality,
  CreateAiModelInput,
  UpdateAiModelConfigInput,
} from '@sofia/shared-types';

export interface AiModelsManagementViewProps {
  onTestModel?: (modelCode: string) => void;
}

interface PlanOption {
  id: string;
  name: string;
}

const DEFAULT_PLANS: PlanOption[] = [
  { id: 'plan_free_trial', name: 'Free Trial' },
  { id: 'plan_pro_monthly', name: 'Pro Monthly' },
  { id: 'plan_enterprise', name: 'Enterprise' },
];

export function getProviderSophiaIcon(providerCode?: string): SophiaDefaultIconName {
  if (!providerCode) return 'processor';
  const code = providerCode.toLowerCase();
  if (code.includes('openai') || code.includes('gpt') || code.includes('o1')) return 'processor';
  if (code.includes('anthropic') || code.includes('claude')) return 'mind';
  if (code.includes('gemini') || code.includes('google')) return 'robot';
  if (code.includes('deepseek')) return 'algorithm';
  if (code.includes('fal') || code.includes('flux') || code.includes('stability')) return 'vision';
  if (code.includes('elevenlabs') || code.includes('voice')) return 'voice-recognition';
  if (code.includes('kling') || code.includes('runway') || code.includes('luma')) return 'camera-360';
  if (code.includes('cloudflare') || code.includes('workers')) return 'server';
  return 'processor';
}

export const MODALITY_SOPHIA_ICONS: Record<string, SophiaDefaultIconName> = {
  text: 'brainstorm',
  chat: 'chat-bot',
  image: 'vision',
  video: 'camera-360',
  speech: 'voice-recognition',
  stt: 'voice-recognition',
  embedding: 'nano-tech',
};

export const AiModelsManagementView: React.FC<AiModelsManagementViewProps> = ({ onTestModel }) => {
  const { t } = useI18n();
  const queryClient = useQueryClient();

  // Filters state
  const [providerFilter, setProviderFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Modals state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editingModel, setEditingModel] = useState<AdminAiModel | null>(null);
  const [deletingModel, setDeletingModel] = useState<AdminAiModel | null>(null);
  const [toastMessage, setToastMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const showToast = (type: 'success' | 'error', text: string) => {
    setToastMessage({ type, text });
    setTimeout(() => setToastMessage(null), 4000);
  };

  // 1. Fetch AI Models list
  const {
    data: modelsData,
    isLoading: isLoadingModels,
    error: modelsError,
    refetch: refetchModels,
    isFetching,
  } = useQuery({
    queryKey: ['admin-ai-models', providerFilter, typeFilter, statusFilter, searchQuery],
    queryFn: async () => {
      const res = await adminClient.get('/api/v1/admin/ai-models', {
        providerId: providerFilter || undefined,
        type: typeFilter || undefined,
        isActive: statusFilter === 'all' ? undefined : statusFilter,
        search: searchQuery || undefined,
      });
      return (res?.data || []) as AdminAiModel[];
    },
  });

  // 2. Fetch AI Providers list
  const { data: providersData } = useQuery({
    queryKey: ['admin-ai-providers'],
    queryFn: async () => {
      const res = await adminClient.get('/api/v1/admin/ai-models/providers');
      return (res?.data || []) as AdminAiProvider[];
    },
  });

  // 3. Fetch Subscription Plans list
  const { data: plansData } = useQuery({
    queryKey: ['public-plans-options'],
    queryFn: async () => {
      try {
        const res = await adminClient.get('/api/v1/plans');
        const items = res?.data || res || [];
        return (items.length > 0 ? items : DEFAULT_PLANS) as PlanOption[];
      } catch {
        return DEFAULT_PLANS;
      }
    },
  });

  const modelsList = modelsData || [];
  const providersList = providersData || [];
  const availablePlans = plansData || DEFAULT_PLANS;

  // Mutations
  const updateMutation = useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: UpdateAiModelConfigInput }) => {
      return adminClient.patch(`/api/v1/admin/ai-models/${id}`, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-ai-models'] });
      setEditingModel(null);
      showToast('success', t('admin.aiModels.successUpdate'));
    },
    onError: (err: any) => {
      showToast('error', err?.message || 'Failed to update AI model.');
    },
  });

  const createMutation = useMutation({
    mutationFn: async (payload: CreateAiModelInput) => {
      return adminClient.post('/api/v1/admin/ai-models', payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-ai-models'] });
      setIsCreateModalOpen(false);
      showToast('success', t('admin.aiModels.successCreate'));
    },
    onError: (err: any) => {
      showToast('error', err?.message || 'Failed to create AI model.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      return adminClient.delete(`/api/v1/admin/ai-models/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-ai-models'] });
      setDeletingModel(null);
      showToast('success', t('admin.aiModels.successDelete'));
    },
    onError: (err: any) => {
      showToast('error', err?.message || 'Failed to delete AI model.');
    },
  });

  const handleToggleActive = (model: AdminAiModel) => {
    updateMutation.mutate({
      id: model.id,
      payload: { isActive: !model.isActive },
    });
  };

  // Table Columns Definition
  const columns = useMemo<ColumnDef<AdminAiModel>[]>(() => [
    {
      id: 'model',
      header: t('admin.aiModels.colModel'),
      accessorKey: 'name',
      cell: ({ row }) => (
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-xs text-foreground">{row.name}</span>
            {row.isDefault && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono font-bold bg-amber-500/10 text-amber-500 border border-amber-500/20">
                <Star className="w-2.5 h-2.5 fill-current" />
                {t('admin.aiModels.defaultBadge')}
              </span>
            )}
          </div>
          <span className="font-mono text-[11px] text-muted-fg">{row.code}</span>
        </div>
      ),
    },
    {
      id: 'provider',
      header: t('admin.aiModels.colProvider'),
      accessorKey: 'providerName',
      cell: ({ row }) => {
        const pCode = row.providerCode || 'ai';
        const brandColor = getBrandColor(pCode);
        const iconName = getProviderSophiaIcon(pCode);
        return (
          <div className="flex items-center gap-2">
            <div
              className="p-1 rounded-md border shrink-0 flex items-center justify-center"
              style={{
                backgroundColor: `${brandColor}15`,
                borderColor: `${brandColor}30`,
                color: brandColor,
              }}
            >
              <SophiaIcon name={iconName} size={13} />
            </div>
            <span className="text-xs font-medium text-foreground">
              {row.providerName || pCode.toUpperCase()}
            </span>
          </div>
        );
      },
    },
    {
      id: 'type',
      header: t('admin.aiModels.colType'),
      accessorKey: 'type',
      cell: ({ value }) => {
        const iconName = MODALITY_SOPHIA_ICONS[value] || 'brainstorm';
        return (
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[11px] font-medium bg-surface-elevated text-foreground border border-surface-border">
            <SophiaIcon name={iconName} size={12} className="text-muted-fg shrink-0" />
            <span className="capitalize">{value}</span>
          </span>
        );
      },
    },
    {
      id: 'pricing',
      header: t('admin.aiModels.colPricing'),
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex flex-col text-[11px] font-mono text-muted-fg leading-tight">
          <span>In: ${row.inputPricePer1k.toFixed(4)}/1k</span>
          <span>Out: ${row.outputPricePer1k.toFixed(4)}/1k</span>
        </div>
      ),
    },
    {
      id: 'creditMultiplier',
      header: t('admin.aiModels.colMultiplier'),
      accessorKey: 'creditMultiplier',
      cell: ({ value }) => (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-mono font-bold bg-primary/10 text-primary border border-primary/20">
          {Number(value).toFixed(2)}x
        </span>
      ),
    },
    {
      id: 'contextWindow',
      header: t('admin.aiModels.colContext'),
      accessorKey: 'contextWindow',
      cell: ({ value }) => (
        <span className="text-xs font-mono text-muted-fg">
          {value >= 1000 ? `${Math.round(value / 1000)}k` : value} tok
        </span>
      ),
    },
    {
      id: 'allowedPlans',
      header: t('admin.aiModels.colPlans'),
      enableSorting: false,
      cell: ({ row }) => {
        const plans = row.allowedPlanIds || [];
        if (plans.length === 0) {
          return (
            <span className="text-[11px] text-muted-fg italic">
              {t('admin.aiModels.allPlans')}
            </span>
          );
        }
        return (
          <div className="flex flex-wrap gap-1 max-w-[180px]">
            {plans.map((pId) => {
              const matched = availablePlans.find((p) => p.id === pId);
              const label = matched ? matched.name : pId.replace('plan_', '');
              return (
                <span
                  key={pId}
                  className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-accent/10 text-accent border border-accent/20 truncate"
                >
                  {label}
                </span>
              );
            })}
          </div>
        );
      },
    },
    {
      id: 'isActive',
      header: t('admin.aiModels.colStatus'),
      accessorKey: 'isActive',
      cell: ({ row, value }) => (
        <button
          type="button"
          onClick={() => handleToggleActive(row)}
          className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border transition-colors ${
            value
              ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20 hover:bg-emerald-500/20'
              : 'bg-muted-fg/10 text-muted-fg border-muted-fg/20 hover:bg-muted-fg/20'
          }`}
          title={t('admin.aiModels.toggleActive')}
        >
          {value ? (
            <>
              <CheckCircle2 className="w-3.5 h-3.5" />
              <span>{t('admin.aiModels.active')}</span>
            </>
          ) : (
            <>
              <XCircle className="w-3.5 h-3.5" />
              <span>{t('admin.aiModels.inactive')}</span>
            </>
          )}
        </button>
      ),
    },
    {
      id: 'actions',
      header: t('admin.aiModels.colActions'),
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex items-center gap-1.5">
          {onTestModel && (
            <button
              type="button"
              onClick={() => onTestModel(row.code)}
              className="p-1.5 rounded-lg border border-surface-border bg-surface hover:bg-primary/10 text-muted-fg hover:text-primary hover:border-primary/30 transition-colors"
              title="Test in AI Sandbox"
            >
              <SophiaIcon name="flask" size={14} />
            </button>
          )}
          <button
            type="button"
            onClick={() => setEditingModel(row)}
            className="p-1.5 rounded-lg border border-surface-border bg-surface hover:bg-surface-elevated text-foreground hover:text-primary transition-colors"
            title={t('admin.aiModels.editConfig')}
          >
            <Edit2 className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            onClick={() => setDeletingModel(row)}
            className="p-1.5 rounded-lg border border-surface-border bg-surface hover:bg-destructive/10 text-muted-fg hover:text-destructive hover:border-destructive/30 transition-colors"
            title={t('admin.aiModels.deleteModel')}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      ),
    },
  ], [availablePlans, t, onTestModel]);

  return (
    <div className="space-y-6">
      {/* Toast Notification */}
      {toastMessage && (
        <div
          className={`fixed top-5 right-5 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-xl text-xs font-medium border animate-in fade-in slide-in-from-top-3 ${
            toastMessage.type === 'success'
              ? 'bg-emerald-950/90 text-emerald-200 border-emerald-500/30'
              : 'bg-destructive/90 text-destructive-foreground border-destructive'
          }`}
        >
          {toastMessage.type === 'success' ? (
            <Check className="w-4 h-4 text-emerald-400" />
          ) : (
            <AlertTriangle className="w-4 h-4" />
          )}
          <span>{toastMessage.text}</span>
        </div>
      )}

      {/* Header Banner */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-6 rounded-2xl bg-surface border border-surface-border relative overflow-hidden">
        <div className="space-y-1 z-10">
          <div className="flex items-center gap-2 text-primary text-xs font-mono font-semibold uppercase tracking-wider">
            <SophiaIcon name="processor" size={16} className="text-primary shrink-0" />
            <span>AI Models & KeyPool Studio</span>
          </div>
          <h2 className="text-xl font-heading font-bold text-foreground">
            {t('admin.aiModels.title')}
          </h2>
          <p className="text-xs text-muted-fg max-w-xl">
            {t('admin.aiModels.subtitle')}
          </p>
        </div>

        <div className="flex items-center gap-2 z-10">
          <button
            type="button"
            onClick={() => refetchModels()}
            disabled={isFetching}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-xl border border-surface-border bg-surface hover:bg-surface-elevated text-foreground transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? 'animate-spin' : ''}`} />
            <span>{t('admin.aiModels.refresh')}</span>
          </button>
          <button
            type="button"
            onClick={() => setIsCreateModalOpen(true)}
            className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-xl bg-primary text-primary-fg hover:bg-primary/90 shadow-glow-primary transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            <span>{t('admin.aiModels.registerBtn')}</span>
          </button>
        </div>

        {/* Ambient background decoration */}
        <div className="absolute right-0 top-0 w-80 h-full bg-gradient-to-l from-primary/5 via-accent/5 to-transparent pointer-events-none" />
      </div>

      {/* AI Providers Architecture Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
        {[
          { code: 'openai', name: 'OpenAI', icon: 'processor' as const, color: '#10a37f' },
          { code: 'anthropic', name: 'Anthropic', icon: 'mind' as const, color: '#d97706' },
          { code: 'gemini', name: 'Gemini', icon: 'robot' as const, color: '#3b82f6' },
          { code: 'deepseek', name: 'DeepSeek', icon: 'algorithm' as const, color: '#06b6d4' },
          { code: 'fal', name: 'fal.ai', icon: 'vision' as const, color: '#a855f7' },
          { code: 'elevenlabs', name: 'ElevenLabs', icon: 'voice-recognition' as const, color: '#f59e0b' },
          { code: 'kling', name: 'Kling Video', icon: 'camera-360' as const, color: '#ec4899' },
          { code: 'cloudflare', name: 'Workers AI', icon: 'server' as const, color: '#f97316' },
        ].map((p) => {
          const isSelected = providerFilter.toLowerCase().includes(p.code);
          return (
            <button
              key={p.code}
              type="button"
              onClick={() => setProviderFilter(isSelected ? '' : p.code)}
              className={`p-3 rounded-xl border text-left transition-all flex flex-col justify-between gap-2 group ${
                isSelected
                  ? 'bg-primary/10 border-primary shadow-xs'
                  : 'bg-surface border-surface-border hover:bg-surface-elevated/70'
              }`}
            >
              <div className="flex items-center justify-between">
                <div
                  className="p-1.5 rounded-lg border shrink-0 flex items-center justify-center"
                  style={{
                    backgroundColor: `${p.color}15`,
                    borderColor: `${p.color}30`,
                    color: p.color,
                  }}
                >
                  <SophiaIcon name={p.icon} size={16} />
                </div>
                <span
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ backgroundColor: p.color }}
                />
              </div>
              <div>
                <span className="text-xs font-semibold text-foreground truncate block">
                  {p.name}
                </span>
                <span className="text-[10px] text-muted-fg font-mono uppercase">
                  {p.code}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Filter Toolbar */}
      <div className="p-4 rounded-xl bg-surface border border-surface-border flex flex-wrap items-center gap-3">
        {/* Search */}
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-muted-fg absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('admin.aiModels.searchPlaceholder')}
            className="w-full pl-9 pr-3 py-1.5 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground placeholder:text-muted-fg focus:outline-none focus:border-primary/50"
          />
        </div>

        {/* Provider Filter */}
        <select
          value={providerFilter}
          onChange={(e) => setProviderFilter(e.target.value)}
          className="px-3 py-1.5 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary/50"
        >
          <option value="">{t('admin.aiModels.filterAllProviders')}</option>
          {providersList.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>

        {/* Modality Filter */}
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
          className="px-3 py-1.5 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary/50"
        >
          <option value="">{t('admin.aiModels.filterAllTypes')}</option>
          <option value="text">Text (LLM)</option>
          <option value="chat">Chat</option>
          <option value="image">Image Generation</option>
          <option value="video">Video Generation</option>
          <option value="speech">Speech / TTS</option>
          <option value="stt">Speech-to-Text</option>
          <option value="embedding">Vector Embedding</option>
        </select>

        {/* Status Filter */}
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3 py-1.5 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary/50"
        >
          <option value="all">{t('admin.aiModels.filterAllStatuses')}</option>
          <option value="true">{t('admin.aiModels.active')}</option>
          <option value="false">{t('admin.aiModels.inactive')}</option>
        </select>
      </div>

      {/* TanStack Data Table */}
      <div className="bg-surface rounded-2xl border border-surface-border overflow-hidden">
        <DataTable
          columns={columns}
          data={modelsList}
          isLoading={isLoadingModels}
          error={modelsError ? t('admin.aiModels.errorLoading') : null}
          enableSearch={false}
          enableCsvExport={true}
          exportFileName="sophia_ai_models_catalog.csv"
        />
      </div>

      {/* Edit Model Modal */}
      {editingModel && (
        <EditAiModelModal
          model={editingModel}
          providers={providersList}
          plans={availablePlans}
          isOpen={true}
          isLoading={updateMutation.isPending}
          onClose={() => setEditingModel(null)}
          onSave={(payload) => updateMutation.mutate({ id: editingModel.id, payload })}
        />
      )}

      {/* Create Model Modal */}
      {isCreateModalOpen && (
        <CreateAiModelModal
          providers={providersList}
          plans={availablePlans}
          isOpen={true}
          isLoading={createMutation.isPending}
          onClose={() => setIsCreateModalOpen(false)}
          onCreate={(payload) => createMutation.mutate(payload)}
        />
      )}

      {/* Delete Confirmation Modal */}
      {deletingModel && (
        <DeleteConfirmModal
          model={deletingModel}
          isLoading={deleteMutation.isPending}
          onClose={() => setDeletingModel(null)}
          onConfirm={() => deleteMutation.mutate(deletingModel.id)}
        />
      )}
    </div>
  );
};

// --- Sub-Component: Edit AI Model Modal ---
interface EditAiModelModalProps {
  model: AdminAiModel;
  providers: AdminAiProvider[];
  plans: PlanOption[];
  isOpen: boolean;
  isLoading: boolean;
  onClose: () => void;
  onSave: (payload: UpdateAiModelConfigInput) => void;
}

const EditAiModelModal: React.FC<EditAiModelModalProps> = ({
  model,
  plans,
  isLoading,
  onClose,
  onSave,
}) => {
  const { t } = useI18n();

  const [name, setName] = useState(model.name);
  const [type, setType] = useState<AiModelModality>(model.type as AiModelModality);
  const [creditMultiplier, setCreditMultiplier] = useState(model.creditMultiplier);
  const [contextWindow, setContextWindow] = useState(model.contextWindow);
  const [inputPricePer1k, setInputPricePer1k] = useState(model.inputPricePer1k);
  const [outputPricePer1k, setOutputPricePer1k] = useState(model.outputPricePer1k);
  const [isActive, setIsActive] = useState(model.isActive);
  const [isDefault, setIsDefault] = useState(model.isDefault);
  const [allowedPlanIds, setAllowedPlanIds] = useState<string[]>(model.allowedPlanIds || []);

  const handleTogglePlan = (planId: string) => {
    setAllowedPlanIds((prev) =>
      prev.includes(planId) ? prev.filter((id) => id !== planId) : [...prev, planId],
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      name,
      type,
      creditMultiplier: Number(creditMultiplier),
      contextWindow: Number(contextWindow),
      inputPricePer1k: Number(inputPricePer1k),
      outputPricePer1k: Number(outputPricePer1k),
      isActive,
      isDefault,
      allowedPlanIds,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in">
      <div className="w-full max-w-xl bg-surface border border-surface-border rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-border bg-surface-elevated">
          <div className="flex items-center gap-2">
            <Sliders className="w-5 h-5 text-primary" />
            <h3 className="text-sm font-heading font-bold text-foreground">
              {t('admin.aiModels.modalEditTitle')}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-muted-fg hover:text-foreground hover:bg-surface transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Modal Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] font-semibold text-muted-fg mb-1">
                {t('admin.aiModels.fieldCode')}
              </label>
              <input
                type="text"
                disabled
                value={model.code}
                className="w-full px-3 py-2 text-xs rounded-lg bg-surface-border/20 border border-surface-border text-muted-fg font-mono cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-muted-fg mb-1">
                {t('admin.aiModels.fieldName')}
              </label>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-[11px] font-semibold text-muted-fg mb-1">
                {t('admin.aiModels.fieldType')}
              </label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value as AiModelModality)}
                className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
              >
                <option value="text">text</option>
                <option value="chat">chat</option>
                <option value="image">image</option>
                <option value="video">video</option>
                <option value="speech">speech</option>
                <option value="stt">stt</option>
                <option value="embedding">embedding</option>
              </select>
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-muted-fg mb-1">
                {t('admin.aiModels.fieldMultiplier')}
              </label>
              <input
                type="number"
                step="0.05"
                min="0"
                required
                value={creditMultiplier}
                onChange={(e) => setCreditMultiplier(Number(e.target.value))}
                className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
              />
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-muted-fg mb-1">
                {t('admin.aiModels.fieldContext')}
              </label>
              <input
                type="number"
                min="0"
                required
                value={contextWindow}
                onChange={(e) => setContextWindow(Number(e.target.value))}
                className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] font-semibold text-muted-fg mb-1">
                {t('admin.aiModels.fieldInputPrice')}
              </label>
              <input
                type="number"
                step="0.0001"
                min="0"
                value={inputPricePer1k}
                onChange={(e) => setInputPricePer1k(Number(e.target.value))}
                className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
              />
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-muted-fg mb-1">
                {t('admin.aiModels.fieldOutputPrice')}
              </label>
              <input
                type="number"
                step="0.0001"
                min="0"
                value={outputPricePer1k}
                onChange={(e) => setOutputPricePer1k(Number(e.target.value))}
                className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
              />
            </div>
          </div>

          {/* Toggles */}
          <div className="pt-2 border-t border-surface-border space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="rounded border-surface-border text-primary focus:ring-primary"
              />
              <span className="text-xs text-foreground font-medium">
                {t('admin.aiModels.fieldActive')}
              </span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                className="rounded border-surface-border text-primary focus:ring-primary"
              />
              <span className="text-xs text-foreground font-medium">
                {t('admin.aiModels.fieldDefault')}
              </span>
            </label>
          </div>

          {/* Plan Entitlements */}
          <div className="pt-2 border-t border-surface-border">
            <label className="block text-[11px] font-semibold text-muted-fg mb-2">
              {t('admin.aiModels.fieldPlans')}
            </label>
            <div className="grid grid-cols-2 gap-2">
              {plans.map((p) => {
                const checked = allowedPlanIds.includes(p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => handleTogglePlan(p.id)}
                    className={`flex items-center justify-between px-3 py-2 rounded-xl text-xs font-medium border transition-colors ${
                      checked
                        ? 'bg-primary/10 border-primary text-primary'
                        : 'bg-surface-elevated border-surface-border text-muted-fg hover:text-foreground'
                    }`}
                  >
                    <span>{p.name}</span>
                    {checked && <Check className="w-3.5 h-3.5" />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 pt-4 border-t border-surface-border">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-xs font-medium rounded-xl border border-surface-border text-muted-fg hover:text-foreground hover:bg-surface-elevated transition-colors"
            >
              {t('admin.aiModels.cancel')}
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-5 py-2 text-xs font-semibold rounded-xl bg-primary text-primary-fg hover:bg-primary/90 shadow-glow-primary transition-colors disabled:opacity-50"
            >
              {isLoading ? t('admin.aiModels.saving') : t('admin.aiModels.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// --- Sub-Component: Create AI Model Modal ---
interface CreateAiModelModalProps {
  providers: AdminAiProvider[];
  plans: PlanOption[];
  isOpen: boolean;
  isLoading: boolean;
  onClose: () => void;
  onCreate: (payload: CreateAiModelInput) => void;
}

const CreateAiModelModal: React.FC<CreateAiModelModalProps> = ({
  providers,
  plans,
  isLoading,
  onClose,
  onCreate,
}) => {
  const { t } = useI18n();

  const [providerId, setProviderId] = useState(providers[0]?.id || '');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [type, setType] = useState<AiModelModality>('text');
  const [creditMultiplier, setCreditMultiplier] = useState(1.0);
  const [contextWindow, setContextWindow] = useState(128000);
  const [inputPricePer1k, setInputPricePer1k] = useState(0.005);
  const [outputPricePer1k, setOutputPricePer1k] = useState(0.015);
  const [isActive, setIsActive] = useState(true);
  const [isDefault, setIsDefault] = useState(false);
  const [allowedPlanIds, setAllowedPlanIds] = useState<string[]>([]);

  const handleTogglePlan = (planId: string) => {
    setAllowedPlanIds((prev) =>
      prev.includes(planId) ? prev.filter((id) => id !== planId) : [...prev, planId],
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onCreate({
      providerId: providerId || providers[0]?.id || '',
      code: code.trim().toLowerCase(),
      name: name.trim(),
      type,
      creditMultiplier: Number(creditMultiplier),
      contextWindow: Number(contextWindow),
      inputPricePer1k: Number(inputPricePer1k),
      outputPricePer1k: Number(outputPricePer1k),
      isActive,
      isDefault,
      allowedPlanIds,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in">
      <div className="w-full max-w-xl bg-surface border border-surface-border rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-border bg-surface-elevated">
          <div className="flex items-center gap-2">
            <Plus className="w-5 h-5 text-primary" />
            <h3 className="text-sm font-heading font-bold text-foreground">
              {t('admin.aiModels.modalCreateTitle')}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-muted-fg hover:text-foreground hover:bg-surface transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] font-semibold text-muted-fg mb-1">
                {t('admin.aiModels.fieldProvider')}
              </label>
              <select
                required
                value={providerId}
                onChange={(e) => setProviderId(e.target.value)}
                className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
              >
                {providers.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-muted-fg mb-1">
                {t('admin.aiModels.fieldCode')}
              </label>
              <input
                type="text"
                required
                placeholder="e.g. gpt-4o-mini, claude-3-5-haiku"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground font-mono focus:outline-none focus:border-primary"
              />
            </div>
          </div>

          <div>
            <label className="block text-[11px] font-semibold text-muted-fg mb-1">
              {t('admin.aiModels.fieldName')}
            </label>
            <input
              type="text"
              required
              placeholder="e.g. GPT-4o Mini (Ultra-Fast)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
            />
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-[11px] font-semibold text-muted-fg mb-1">
                {t('admin.aiModels.fieldType')}
              </label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value as AiModelModality)}
                className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
              >
                <option value="text">text</option>
                <option value="chat">chat</option>
                <option value="image">image</option>
                <option value="video">video</option>
                <option value="speech">speech</option>
                <option value="stt">stt</option>
                <option value="embedding">embedding</option>
              </select>
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-muted-fg mb-1">
                {t('admin.aiModels.fieldMultiplier')}
              </label>
              <input
                type="number"
                step="0.05"
                min="0"
                required
                value={creditMultiplier}
                onChange={(e) => setCreditMultiplier(Number(e.target.value))}
                className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
              />
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-muted-fg mb-1">
                {t('admin.aiModels.fieldContext')}
              </label>
              <input
                type="number"
                min="0"
                required
                value={contextWindow}
                onChange={(e) => setContextWindow(Number(e.target.value))}
                className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[11px] font-semibold text-muted-fg mb-1">
                {t('admin.aiModels.fieldInputPrice')}
              </label>
              <input
                type="number"
                step="0.0001"
                min="0"
                value={inputPricePer1k}
                onChange={(e) => setInputPricePer1k(Number(e.target.value))}
                className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
              />
            </div>

            <div>
              <label className="block text-[11px] font-semibold text-muted-fg mb-1">
                {t('admin.aiModels.fieldOutputPrice')}
              </label>
              <input
                type="number"
                step="0.0001"
                min="0"
                value={outputPricePer1k}
                onChange={(e) => setOutputPricePer1k(Number(e.target.value))}
                className="w-full px-3 py-2 text-xs rounded-lg bg-surface-elevated border border-surface-border text-foreground focus:outline-none focus:border-primary"
              />
            </div>
          </div>

          {/* Toggles */}
          <div className="pt-2 border-t border-surface-border space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="rounded border-surface-border text-primary focus:ring-primary"
              />
              <span className="text-xs text-foreground font-medium">
                {t('admin.aiModels.fieldActive')}
              </span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                className="rounded border-surface-border text-primary focus:ring-primary"
              />
              <span className="text-xs text-foreground font-medium">
                {t('admin.aiModels.fieldDefault')}
              </span>
            </label>
          </div>

          {/* Plan Entitlements */}
          <div className="pt-2 border-t border-surface-border">
            <label className="block text-[11px] font-semibold text-muted-fg mb-2">
              {t('admin.aiModels.fieldPlans')}
            </label>
            <div className="grid grid-cols-2 gap-2">
              {plans.map((p) => {
                const checked = allowedPlanIds.includes(p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => handleTogglePlan(p.id)}
                    className={`flex items-center justify-between px-3 py-2 rounded-xl text-xs font-medium border transition-colors ${
                      checked
                        ? 'bg-primary/10 border-primary text-primary'
                        : 'bg-surface-elevated border-surface-border text-muted-fg hover:text-foreground'
                    }`}
                  >
                    <span>{p.name}</span>
                    {checked && <Check className="w-3.5 h-3.5" />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 pt-4 border-t border-surface-border">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 text-xs font-medium rounded-xl border border-surface-border text-muted-fg hover:text-foreground hover:bg-surface-elevated transition-colors"
            >
              {t('admin.aiModels.cancel')}
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-5 py-2 text-xs font-semibold rounded-xl bg-primary text-primary-fg hover:bg-primary/90 shadow-glow-primary transition-colors disabled:opacity-50"
            >
              {isLoading ? t('admin.aiModels.saving') : t('admin.aiModels.create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// --- Sub-Component: Delete Confirmation Modal ---
interface DeleteConfirmModalProps {
  model: AdminAiModel;
  isLoading: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const DeleteConfirmModal: React.FC<DeleteConfirmModalProps> = ({
  model,
  isLoading,
  onClose,
  onConfirm,
}) => {
  const { t } = useI18n();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in">
      <div className="w-full max-w-md bg-surface border border-surface-border rounded-2xl shadow-2xl overflow-hidden p-6 space-y-4">
        <div className="w-12 h-12 rounded-2xl bg-destructive/10 border border-destructive/20 flex items-center justify-center text-destructive mx-auto">
          <AlertTriangle className="w-6 h-6" />
        </div>

        <div className="text-center space-y-1">
          <h3 className="text-base font-heading font-bold text-foreground">
            {t('admin.aiModels.confirmDeleteTitle')}
          </h3>
          <p className="text-xs text-muted-fg">
            {t('admin.aiModels.confirmDeleteDesc', { code: model.code })}
          </p>
        </div>

        <div className="p-3 rounded-xl bg-surface-elevated border border-surface-border text-xs space-y-1">
          <div className="flex justify-between text-muted-fg">
            <span>Name:</span>
            <span className="font-semibold text-foreground">{model.name}</span>
          </div>
          <div className="flex justify-between text-muted-fg">
            <span>Code:</span>
            <span className="font-mono text-foreground">{model.code}</span>
          </div>
          <div className="flex justify-between text-muted-fg">
            <span>Provider:</span>
            <span className="text-foreground">{model.providerName || model.providerId}</span>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-xs font-medium rounded-xl border border-surface-border text-muted-fg hover:text-foreground hover:bg-surface-elevated transition-colors"
          >
            {t('admin.aiModels.cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isLoading}
            className="px-5 py-2 text-xs font-semibold rounded-xl bg-destructive text-destructive-foreground hover:bg-destructive/90 transition-colors disabled:opacity-50"
          >
            {isLoading ? t('admin.aiModels.deleting') : t('admin.aiModels.deleteModel')}
          </button>
        </div>
      </div>
    </div>
  );
};
